/**
 * Origin-folder detection for the Preview share feature (task #29).
 *
 * PM projects in Dropbox contain delivery folders whose names vary in
 * spelling: "Finals", "Final", "zur_Abnahme", "zur Abnahme",
 * "zur-Abnahme", "Abnahme", "Preview", "Previews"… We classify any
 * folder (or any ancestor folder of a file path) into one of three
 * origin labels so the PM — and later the client — always sees clearly
 * whether a file is a FINAL, an approval copy, or a preview.
 *
 * Matching is case-insensitive and ignores space/underscore/hyphen
 * differences. Shared between server and client.
 */

export type OriginLabel = 'final' | 'abnahme' | 'preview' | 'other';

/** Normalized (lowercase, separator-stripped) name → label. */
const FOLDER_LABELS: Record<string, OriginLabel> = {
  final: 'final',
  finals: 'final',
  abnahme: 'abnahme',
  zurabnahme: 'abnahme',
  abnahmen: 'abnahme',
  preview: 'preview',
  previews: 'preview',
  vorschau: 'preview',
};

function normalizeFolderName(name: string): string {
  return name
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s_\-.]+/g, '');
}

/** Classify a single folder name. */
export function classifyFolderName(name: string): OriginLabel {
  return FOLDER_LABELS[normalizeFolderName(name)] ?? 'other';
}

/**
 * Classify a full file path by its ancestor folders. The DEEPEST
 * labeled ancestor wins (e.g. `/…/Finals/zur_Abnahme/x.mp4` → abnahme).
 */
export function classifyPathOrigin(path: string): OriginLabel {
  const segments = path.split('/').filter(Boolean);
  // Exclude the file name itself.
  for (let i = segments.length - 2; i >= 0; i--) {
    const label = classifyFolderName(segments[i]);
    if (label !== 'other') return label;
  }
  return 'other';
}

/** Display strings for badges (kept English per user preference). */
export const ORIGIN_LABEL_DISPLAY: Record<OriginLabel, string> = {
  final: 'FINAL',
  abnahme: 'ZUR ABNAHME',
  preview: 'PREVIEW',
  other: 'FILE',
};

/** True when a folder name is one of the known delivery folders. */
export function isDeliveryFolder(name: string): boolean {
  return classifyFolderName(name) !== 'other';
}
